/**
 * Los cuatro momentos del arranque del panel.
 *
 * 🚨 POR QUE EXISTE. El founder dijo cinco veces que la pantalla "sigue con el loading". Cada vez se
 *    eliminó un sospechoso —el conteo exacto, los cuatro viajes en fila, el editor de texto, Sentry,
 *    24 páginas cargadas de entrada, la librería de gráficos, la precarga rota— y cada vez el número
 *    bajó y el síntoma siguió. Eso es adivinar con buena puntería, no medir.
 *
 *    El RUM sabía CUÁNTO tardaba (947 ms) y no sabía EN QUÉ.
 *
 * ⭐ ACÁ SE ANOTA CUÁNDO PASÓ CADA COSA, y la resta se hace después en una consulta:
 *
 *      js       el JavaScript terminó de interpretarse y React puede montar
 *      sesion   la sesión quedó resuelta: recién acá se sabe de qué negocio pedir
 *      pedido   la primera consulta de la pantalla salió a la red
 *      filas    la lista pintó sus filas
 *
 * 🪤 SE GUARDA EL MOMENTO, NO LA DURACIÓN. Guardar duraciones obligaría a decidir en el navegador
 *    contra qué se resta cada una, y ese cálculo se equivoca apenas haya una navegación suave en el
 *    medio. Restar es trabajo de la consulta.
 *
 * 🪤 SÓLO LA PRIMERA VEZ. Un hito describe el arranque; si se pisara en cada navegación mediría
 *    cualquier cosa menos lo que dice medir. La segunda llamada se ignora en silencio.
 *
 * 🪤 Y NO IMPORTA NADA PESADO. Este módulo entra al arranque por definición: si arrastrara la
 *    telemetría entera, el instrumento cambiaría lo que vino a medir.
 */

export type NombreDeHito =
  | 'js' | 'sesion' | 'pedido' | 'datos' | 'permisos' | 'chunk' | 'cascaron'
  | 'barra' | 'marco' | 'montado' | 'filas';

/**
 * Lo que YA ocurrio. Nunca se vacia.
 *
 * 🚨 SIN ESTO EL INSTRUMENTO MENTIA. La guarda de "solo la primera vez" miraba la cola de envio, y
 *    esa cola se vacia al mandar — asi que despues de cada envio el hito volvia a considerarse
 *    nuevo y se remarcaba. En la medicion se vio como `filas` siete veces: 2.221, 4.412, 5.721,
 *    7.171, 8.641, 9.867 ms. No eran cargas lentas: era el mismo hito anotandose de nuevo.
 *
 * 🪤 Dos estructuras porque son dos preguntas distintas: "¿ya paso?" (permanente) y "¿falta
 *    mandarlo?" (temporal). Usar una sola para las dos fue exactamente el error.
 */
const vistos = new Set<NombreDeHito>();

/** Lo anotado que todavia no se mando. Se vacia al enviar. */
const momentos = new Map<NombreDeHito, number>();

/** Anota cuándo ocurrió un hito, medido desde que arrancó la navegación. */
export function marcarHito(nombre: NombreDeHito): void {
  if (vistos.has(nombre)) return;
  try {
    vistos.add(nombre);
    momentos.set(nombre, performance.now());
    escucha?.();
  } catch {
    /* un instrumento no rompe la app que mide */
  }
}

/**
 * Los hitos que todavía no se enviaron, y los saca de la lista para que no se manden dos veces.
 *
 * 🪤 Devuelve y BORRA en la misma llamada: la sonda puede correr varias veces mientras la sesión se
 *    resuelve, y sin esto cada corrida mandaría de nuevo los mismos momentos.
 */
export function hitosPendientes(): Array<{ nombre: NombreDeHito; ms: number }> {
  const salida: Array<{ nombre: NombreDeHito; ms: number }> = [];
  for (const [nombre, ms] of momentos) salida.push({ nombre, ms });
  momentos.clear();
  return salida;
}

/**
 * Avisar cuando se marca un hito.
 *
 * 🚨 SIN ESTO FALTABA EL ÚLTIMO. La sonda mandaba los hitos UNA vez, cuando la sesión quedaba
 *    resuelta — y `filas` ocurre DESPUÉS de ese momento, así que quedaba pendiente para siempre.
 *    Se veía el arranque hasta la mitad: justo faltaba el tramo entre la consulta y las filas, que
 *    es el que quedó siendo el más largo.
 *
 * 🪤 Un instrumento incompleto es peor que ninguno: da la sensación de haber medido.
 */
type Escucha = () => void;
let escucha: Escucha | null = null;

export function alMarcarHito(cb: Escucha): void {
  escucha = cb;
}
